import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router, NavigationEnd } from "@angular/router";
import { Subject } from "rxjs";
import { filter, finalize, takeUntil } from "rxjs/operators";
import { UserService } from "@/_services";
import { ExportHelperService } from "@/_services/export-helper.service";
import {
    ConversationMessage,
    RiseAgentReportRequest,
    RiseAgentResponse,
    RiseAgentService,
} from "@/_services/rise-agent.service";
import { ChatMessage } from "@/components/rise-agent/rise-agent.types";
import { RiseAgentChartComponent } from "./rise-agent-chart/rise-agent-chart.component";
import { resolveRiseAgentScreenLabel } from "./rise-agent-screen-labels";
import { resolveExportedAnswerBlock } from "./rise-agent-report-block";
import { UserDm } from "@/_models/user-dm";
import UserRoles = UserDm.UserRoles;
import UserModel = UserDm.UserModel;

const RISE_SEEN_KEY_PREFIX = "rise_agent_seen_";

@Component({
    selector: "app-rise-agent-sidebar",
    templateUrl: "./rise-agent-sidebar.component.html",
    styleUrls: ["./rise-agent-sidebar.component.scss"],
})
export class RiseAgentSidebarComponent implements OnDestroy {
    @Input() set visible(value: boolean) {
        if (value && !this._visible) {
            this.onSidebarOpen();
        }
        this._visible = value;
    }
    get visible(): boolean {
        return this._visible;
    }

    @Output() visibleChange = new EventEmitter<boolean>();

    @ViewChild("messagesContainer")
    messagesContainer: ElementRef<HTMLDivElement>;
    @ViewChildren(RiseAgentChartComponent)
    chartComponents: QueryList<RiseAgentChartComponent>;

    get messages(): ChatMessage[] {
        return this.riseAgentService.messages;
    }
    set messages(value: ChatMessage[]) {
        this.riseAgentService.messages = value;
    }

    get showQuickStartChips(): boolean {
        return (
            this.messages.some((m) => m.isGreeting) &&
            !this.messages.some((m) => m.role === "user")
        );
    }

    get quickStartChips(): string[] {
        if (this.isOwner) {
            return [
                "Summarize our safety performance",
                "Show open corrective actions",
                "Analyze recent hazard trends",
            ];
        }
        const isAdmin =
            this.currentUser?.hasAtLeastRole(UserRoles.Admin) ?? false;
        if (isAdmin) {
            return [
                "What can RISE help me with?",
                "Review safety reports",
                "Check risk assessments",
            ];
        }
        return [
            "Show risk trends from my submissions",
            "Help me understand a safety policy",
        ];
    }

    messageControl = new FormControl<string>("", { nonNullable: true });
    isLoading = false;
    currentRecordLabel: string | null = null;

    trialRequestsUsed: number | null = null;
    trialRequestsTotal: number | null = null;

    showTrialConfirm = false;
    private pendingTrialMessage: string | null = null;

    _visible = false;
    displayMode: "overlay" | "pinned" = "overlay";
    private currentUser: UserModel | null = null;
    private isOwner = false;
    private destroy$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private riseAgentService: RiseAgentService,
        private exportHelperService: ExportHelperService,
        private router: Router,
    ) {
        this.userService
            .getCurrentUserAsync()
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => {
                this.currentUser = user;
                this.isOwner = user.hasAtLeastRole(UserRoles.Owner);
            });

        this.currentRecordLabel = this.getRecordLabelForUrl(this.router.url);
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe((e) => {
                this.currentRecordLabel = this.getRecordLabelForUrl(
                    (e as NavigationEnd).urlAfterRedirects,
                );
            });

        /**
         * A question asked from outside the sidebar, such as one of the
         * dashboard's preset prompts. Treated exactly as a typed question so
         * it goes through the same trial and loading handling.
         */
        this.riseAgentService.pendingPrompt$
            .pipe(takeUntil(this.destroy$))
            .subscribe((prompt) => {
                this.messageControl.setValue(prompt);
                this.sendMessage();
            });

        this.riseAgentService.displayMode$
            .pipe(takeUntil(this.destroy$))
            .subscribe((mode) => (this.displayMode = mode));
    }

    @HostListener("window:resize")
    onResize(): void {
        if (
            window.innerWidth < 1024 &&
            this.riseAgentService.displayMode === "pinned"
        ) {
            this.riseAgentService.unpin();
        }
    }

    togglePin(): void {
        if (window.innerWidth < 1024) {
            return;
        }
        this.riseAgentService.togglePin();
    }

    onClose(): void {
        if (this.riseAgentService.displayMode === "pinned") {
            this.riseAgentService.unpin();
        }
        this.onSidebarHide();
    }

    onRecordLinkClick(url: string): void {
        this.riseAgentService.pin();
        this.router.navigateByUrl(url);
    }

    onResourceLinkClick(url: string): void {
        this.riseAgentService.pin();
        this.router.navigateByUrl(url);
    }

    get trialConfirmText(): string {
        const used = this.trialRequestsUsed ?? 0;
        const total = this.trialRequestsTotal ?? 2;
        const remaining = total - used;
        if (remaining <= 1) {
            return `This is your final free RISE intelligence request. Would you like to continue?`;
        }
        return `This may use one of your ${total} free RISE intelligence requests. You've used ${used} so far. Continue?`;
    }

    private get shouldShowTrialConfirm(): boolean {
        return (
            this.trialRequestsTotal != null &&
            this.trialRequestsUsed != null &&
            this.trialRequestsUsed < this.trialRequestsTotal
        );
    }

    sendMessage(): void {
        const text = this.messageControl.value.trim();
        if (!text || this.isLoading) {
            return;
        }

        if (this.shouldShowTrialConfirm) {
            this.pendingTrialMessage = text;
            this.messageControl.reset();
            this.showTrialConfirm = true;
            return;
        }

        this.dispatchMessage(text);
    }

    confirmTrialRequest(): void {
        const text = this.pendingTrialMessage;
        this.showTrialConfirm = false;
        this.pendingTrialMessage = null;
        if (text) {
            this.dispatchMessage(text);
        }
    }

    cancelTrialRequest(): void {
        this.messageControl.setValue(this.pendingTrialMessage ?? "");
        this.pendingTrialMessage = null;
        this.showTrialConfirm = false;
    }

    private dispatchMessage(text: string): void {
        const history = this.buildConversationHistory();
        this.messages.push({ role: "user", content: text });
        this.messageControl.reset();
        this.isLoading = true;
        this.scrollToBottom();

        this.riseAgentService
            .chat(text, history, this.router.url)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                next: (response: RiseAgentResponse) => {
                    if (response.chartData) {
                        this.messages.push({
                            role: "assistant",
                            type: "chart",
                            content: "",
                            chartData: response.chartData,
                        });
                    }

                    this.messages.push({
                        role: "assistant",
                        content: response.response,
                        requiresUpgrade: response.requiresUpgrade,
                        isAnalysis: response.isAnalysis,
                        isExportable: response.isExportable,
                        suggestedActions: response.suggestedActions,
                        chartData: response.chartData,
                        // Phase 2: false when the agent returned a graceful
                        // failure fallback (mirrors RiseAgentResponse.success).
                        success: response.success,
                    });

                    if (response.resourceLinks?.length) {
                        this.messages.push({
                            role: "assistant",
                            type: "resource-links",
                            content: "",
                            resourceLinks: response.resourceLinks,
                        });
                    }

                    if (response.recordLinks?.length) {
                        this.messages.push({
                            role: "assistant",
                            type: "record-links",
                            content: "",
                            recordLinks: response.recordLinks,
                        });
                    }

                    // Offer the safety-report download for any exportable answer
                    // (broad overviews AND targeted data answers). Falls back to
                    // isAnalysis for older responses that predate isExportable.
                    if (
                        (response.isExportable ?? response.isAnalysis) &&
                        !response.requiresUpgrade
                    ) {
                        this.messages.push({
                            role: "assistant",
                            type: "download-prompt",
                            content: "",
                        });
                    }

                    if (response.upgradeMessage && this.isOwner) {
                        this.messages.push({
                            role: "assistant",
                            type: "upgrade-suggestion",
                            content: response.upgradeMessage,
                            upgradePlan: response.upgradePlan,
                        });
                    }

                    if (response.trialRequestsUsed != null) {
                        this.trialRequestsUsed = response.trialRequestsUsed;
                    }
                    if (response.trialRequestsTotal != null) {
                        this.trialRequestsTotal = response.trialRequestsTotal;
                    }

                    if (
                        response.requiresUpgrade &&
                        response.trialRequestsUsed != null &&
                        response.trialRequestsTotal != null &&
                        response.trialRequestsUsed ===
                            response.trialRequestsTotal
                    ) {
                        this.messages.push({
                            role: "assistant",
                            type: "trial-notice",
                            content: `Your team has used all ${response.trialRequestsTotal} free intelligence requests. Upgrade RISE to unlock unrestricted access to safety data analysis.`,
                            trialExhausted: true,
                        });
                    }

                    // #2736: one feedback card, always the last thing in the
                    // response (after any reading / related-items cards).
                    if (
                        response.response &&
                        !response.requiresUpgrade &&
                        response.success !== false
                    ) {
                        this.messages.push({
                            role: "assistant",
                            type: "feedback",
                            content: "",
                            // Bind the rated pair here, where both are known
                            // for certain. Resolving it later by walking back
                            // through the messages would land on whichever
                            // assistant message happens to sit closest to the
                            // card, e.g. the owner upsell, and record the
                            // rating against that text instead of the answer.
                            feedbackQuestion: text,
                            feedbackAnswer: response.response,
                        });
                    }

                    this.scrollToBottom();
                },
                error: () => {
                    // Phase 2: known agent failures return 200 with
                    // success:false and are handled in the next() callback.
                    // This branch now only sees genuine network or server
                    // (5xx) failures.
                    this.messages.push({
                        role: "assistant",
                        content:
                            "The chat service is currently unavailable. Please try again shortly.",
                        success: false,
                    });
                    this.scrollToBottom();
                },
            });
    }

    downloadReport(): void {
        const request = this.buildReportRequest();
        this.exportHelperService.exportHandler(
            this.riseAgentService.downloadReport(request),
        );
    }

    /**
     * #2736: a thumb was clicked. A thumbs-up submits immediately; a
     * thumbs-down first reveals a reason box so the user can say why.
     */
    onFeedbackThumb(msg: ChatMessage, rating: "up" | "down"): void {
        if (msg.feedback || msg.feedbackDraft) {
            return;
        }
        if (rating === "up") {
            this.submitFeedback(msg, "up");
        } else {
            msg.feedbackDraft = "down";
        }
    }

    /** #2736: submit the pending thumbs-down with whatever reason was typed. */
    submitFeedbackDraft(msg: ChatMessage): void {
        this.submitFeedback(msg, "down", msg.feedbackReason);
    }

    /** #2736: dismiss the reason box without rating. */
    cancelFeedbackDraft(msg: ChatMessage): void {
        msg.feedbackDraft = undefined;
        msg.feedbackReason = undefined;
    }

    /**
     * #2736: record a "was this helpful?" rating. The rated question and answer
     * are carried on the feedback card itself, bound when the card was created,
     * so nothing here has to guess which message the rating belongs to.
     */
    submitFeedback(
        msg: ChatMessage,
        rating: "up" | "down",
        reason?: string,
    ): void {
        if (msg.feedback) {
            return;
        }

        const question = msg.feedbackQuestion ?? "";
        const answer = msg.feedbackAnswer ?? msg.content;

        msg.feedback = rating;
        msg.feedbackDraft = undefined;
        this.riseAgentService
            .submitFeedback({
                rating,
                question,
                answer,
                reason: reason?.trim() || undefined,
            })
            .subscribe({
                // Let the user try again if recording failed.
                error: () => (msg.feedback = undefined),
            });
    }

    private buildReportRequest(): RiseAgentReportRequest {
        // Exportable message — last assistant answer eligible for a report
        // (broad OR targeted data answer). Falls back to isAnalysis for older
        // messages that predate isExportable.
        const analysisMsg = [...this.messages]
            .reverse()
            .find(
                (m) =>
                    (m.isExportable ?? m.isAnalysis) && m.role === "assistant",
            );

        // User question — last user message before the analysis
        const analysisIndex = analysisMsg
            ? this.messages.lastIndexOf(analysisMsg)
            : this.messages.length;
        const userQuestion =
            [...this.messages]
                .slice(0, analysisIndex)
                .reverse()
                .find((m) => m.role === "user")?.content ?? "";

        // Chart and links are scoped to the exported answer's own message block,
        // never "the most recent in the chat" — see rise-agent-report-block.ts.
        const block = resolveExportedAnswerBlock(
            this.messages,
            analysisIndex,
            !!analysisMsg,
        );
        // Chart components render in message order, so the block's ordinal is
        // this answer's index into the rendered chart list.
        const chartImage =
            block.chartOrdinal >= 0
                ? (this.chartComponents?.toArray() ?? [])[
                      block.chartOrdinal
                  ]?.getChartImage() ?? ""
                : "";

        return {
            analysisText: analysisMsg?.content ?? "",
            chartImage: chartImage || undefined,
            chartTitle: block.chartTitle || undefined,
            resourceLinks: block.resourceLinks,
            recordLinks: block.recordLinks,
            userQuestion: userQuestion || undefined,
        };
    }

    sendSuggestedAction(action: string): void {
        this.messageControl.setValue(action);
        this.sendMessage();
    }

    onQuickStartChipClick(chip: string): void {
        this.messageControl.setValue(chip);
        this.sendMessage();
    }

    onEnterKey(event: KeyboardEvent): void {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    onSidebarHide(): void {
        this.messageControl.reset();
        this.isLoading = false;
        this._visible = false;
        this.visibleChange.emit(false);
    }

    private async onSidebarOpen(): Promise<void> {
        if (this.riseAgentService.hasGreeted && this.messages.length > 0) {
            this.scrollToBottom();
            return;
        }
        if (!this.currentUser) return;

        const isFirstTime = !this.hasSeenRise(this.currentUser._id);
        this.markAsSeen(this.currentUser._id);

        if (!isFirstTime) {
            this.messages.push({
                role: "assistant",
                type: "privacy-notice",
                content: "",
            });
            this.messages.push({
                role: "assistant",
                content: `Welcome back, ${this.currentUser.firstName}. What can I help you with today?`,
                isGreeting: true,
            });
            this.riseAgentService.hasGreeted = true;
            return;
        }

        this.messages.push({
            role: "assistant",
            type: "privacy-notice",
            content: "",
        });
        for (const content of this.buildGreetingMessages()) {
            this.isLoading = true;
            this.scrollToBottom();
            await this.delay(600);
            if (!this._visible) return;
            this.isLoading = false;
            this.messages.push({
                role: "assistant",
                content,
                isGreeting: true,
            });
            this.scrollToBottom();
        }
        this.riseAgentService.hasGreeted = true;
    }

    startNewChat(): void {
        this.riseAgentService.clearChat();
        this.trialRequestsUsed = null;
        this.trialRequestsTotal = null;
        this.onSidebarOpen();
    }

    private buildGreetingMessages(): string[] {
        return [
            `**Hello, I'm your assistant for Real-time Intelligence for Safety Excellence, but you can call me RISE for short!**`,
            `I'm here to help you get the most out of your safety program. I can walk you through anything in the software, or answer regulatory questions about FAA Part 5, ICAO, and more.`,
            this.isOwner
                ? `As we spend more time together, and if you choose to invest in our relationship, I'll also be able to help your entire team with safety performance analysis, trend identification, and hands-on guidance for risk mitigation and corrective actions.`
                : `As we spend more time together, and if your organization chooses to invest in our relationship, I'll also be able to analyze your safety data, identify trends, and help you make better safety decisions.`,
            `But we can get to that later. What can I help you with today?`,
        ];
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private hasSeenRise(userId: string): boolean {
        return (
            localStorage.getItem(`${RISE_SEEN_KEY_PREFIX}${userId}`) === "true"
        );
    }

    private markAsSeen(userId: string): void {
        localStorage.setItem(`${RISE_SEEN_KEY_PREFIX}${userId}`, "true");
    }

    private buildConversationHistory(): ConversationMessage[] {
        return this.messages
            .filter(
                (msg) =>
                    !msg.isGreeting &&
                    msg.type !== "trial-notice" &&
                    msg.type !== "resource-links" &&
                    msg.type !== "record-links" &&
                    msg.type !== "download-prompt" &&
                    msg.type !== "upgrade-suggestion" &&
                    msg.type !== "chart" &&
                    msg.type !== "privacy-notice" &&
                    msg.type !== "feedback",
            )
            .map((msg) => ({
                role: msg.role,
                content: msg.content,
            }));
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            if (this.messagesContainer) {
                this.messagesContainer.nativeElement.scrollTop =
                    this.messagesContainer.nativeElement.scrollHeight;
            }
        }, 100);
    }

    getRecordTypeLabel(type: string | undefined): string {
        if (!type) return "";
        const RECORD_TYPE_LABELS: Record<string, string> = {
            task: "Corrective Action",
            issue: "Safety Case",
            report: "Report",
            promotion: "Safety Promotion",
            document: "Document",
            internalAudit: "Internal Audit",
            riskEvaluation: "Risk Evaluation",
            hazardRegister: "Hazard Register",
            controlLibrary: "Safety Control",
        };
        return RECORD_TYPE_LABELS[type] ?? type;
    }

    getBadgeStatusClass(status: string): string {
        const s = status.toLowerCase();
        if (s.includes("overdue")) return "rise-card-badge--overdue";
        if (
            s.includes("active") ||
            s.includes("open") ||
            s.includes("in progress")
        )
            return "rise-card-badge--active";
        if (
            s.includes("completed") ||
            s.includes("resolved") ||
            s.includes("closed")
        )
            return "rise-card-badge--completed";
        return "";
    }

    private getRecordLabelForUrl(url: string): string | null {
        return resolveRiseAgentScreenLabel(url);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
